import { Component, EventEmitter, HostListener, Output, computed, inject, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '../../../core/services/auth.service';
import { AuthPromptService } from '../../../core/services/auth-prompt.service';
import { CartService } from '../../../core/services/cart.service';
import { AppLang, LanguageService } from '../../../core/services/language.service';
import { toImageSrc } from '../../utils/image.util';
import { dropdownFade } from '../../animations';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, TranslatePipe],
  templateUrl: './navbar.component.html',
  animations: [dropdownFade]
})
export class NavbarComponent {
  @Output() cartOpen = new EventEmitter<void>();
  @Output() authOpen = new EventEmitter<void>();
  @Output() notificationsOpen = new EventEmitter<void>();

  readonly cartCount = computed(() => this.cartService.totalQuantity());
  readonly logoutConfirmOpen = signal(false);
  readonly searchQuery = signal('');
  readonly profileOpen = signal(false);
  readonly profilePhoto = computed(() => toImageSrc(this.authService.currentUser()?.UsrImageUrl || null));

  private readonly router = inject(Router);
  private readonly authPrompt = inject(AuthPromptService);
  readonly language = inject(LanguageService);

  constructor(
    public readonly authService: AuthService,
    private readonly cartService: CartService
  ) {}

  setLang(lang: AppLang): void {
    this.language.use(lang);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.nav-profile')) {
      this.profileOpen.set(false);
    }
  }

  toggleProfile(): void {
    this.profileOpen.update((v) => !v);
  }

  onNavSearchInput(value: string): void {
    const email = (this.authService.currentUser()?.UsrEmail || '').trim().toLowerCase();
    this.searchQuery.set(email && value.trim().toLowerCase() === email ? '' : value);
  }

  onSearch(event: Event): void {
    event.preventDefault();
    const q = this.searchQuery().trim();
    this.closeMobileMenu();
    void this.router.navigate(['/books'], {
      queryParams: q ? { q } : {},
      queryParamsHandling: '',
      replaceUrl: false
    });
  }

  openNotifications(): void {
    this.notificationsOpen.emit();
  }

  requestLogout(): void {
    this.profileOpen.set(false);
    this.logoutConfirmOpen.set(true);
  }

  cancelLogout(): void {
    this.logoutConfirmOpen.set(false);
  }

  confirmLogout(): void {
    this.authService.logout();
    this.cartService.cartItems.set([]);
    this.authPrompt.clearReturnUrl();
    this.logoutConfirmOpen.set(false);
    void this.router.navigateByUrl('/home');
  }

  closeMobileMenu(): void {
    const cb = document.getElementById('nav-menu-toggle') as HTMLInputElement | null;
    if (cb?.checked) cb.checked = false;
    this.profileOpen.set(false);
  }
}
